import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Check,
  ChevronDown,
  Loader2,
  MessageCircle,
  Plus,
  Star,
} from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import CreateOrganizationWizard from "@/components/CreateOrganizationWizard";
import { markPendingTour } from "@/lib/billingApi";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/apiBase";
import { planDisplayName } from "@/lib/planMarketing";

function OrgPlanBadge({ plan, className }) {
  const label = planDisplayName(plan);
  return (
    <span
      className={cn(
        "shrink-0 rounded-md border border-border/80 bg-muted/50 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground",
        className
      )}
    >
      {label}
    </span>
  );
}

/**
 * Primary navigation control for switching between Social and organization workspaces.
 * Always navigates to /dashboard after a selection change.
 */
const WorkspaceSwitcher = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const {
    selectedOrgId,
    setSelectedOrgId,
    defaultWorkspace,
    setDefaultWorkspacePreference,
  } = useOrgFilter();
  const { state: sidebarState, isMobile, setOpenMobile } = useSidebar();
  const sidebarCollapsed = sidebarState === "collapsed" && !isMobile;

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [defaultError, setDefaultError] = useState("");

  const isSocial =
    selectedOrgId === "social" || selectedOrgId === null || selectedOrgId === "";
  const activeOrg = !isSocial
    ? organizations.find((o) => o.org_id === selectedOrgId)
    : null;
  const activeName = isSocial ? "Circa Social" : activeOrg?.name || "Workspace";
  const activeSubtitle = isSocial
    ? "Personal network"
    : activeOrg
      ? `${planDisplayName(activeOrg.plan)} plan`
      : "Organization workspace";

  const defaultOrgId =
    defaultWorkspace?.type === "organization" ? defaultWorkspace.org_id : null;
  const isSocialDefault = !defaultOrgId;

  const sortedOrganizations = useMemo(() => {
    if (!defaultOrgId) return organizations;
    return [...organizations].sort((a, b) => {
      if (a.org_id === defaultOrgId) return -1;
      if (b.org_id === defaultOrgId) return 1;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [organizations, defaultOrgId]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;
    const fetchOrganizations = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API_BASE_URL}/org/memberships`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) {
          setOrganizations(response.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
        if (!cancelled) {
          setError("Couldn’t load workspaces");
          setOrganizations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOrganizations();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const handleSelect = (orgId) => {
    setMenuOpen(false);
    if (isMobile) {
      setOpenMobile(false);
    }
    // Leave org-scoped routes first so their route→workspace sync effects
    // cannot overwrite this selection while still mounted.
    navigate("/dashboard");
    setSelectedOrgId(orgId);
  };

  const handleSetDefault = async (next) => {
    setDefaultError("");
    try {
      await setDefaultWorkspacePreference(next);
    } catch (err) {
      console.error("Failed to update default workspace:", err);
      setDefaultError(
        err.response?.data?.message ||
          err.message ||
          "Couldn’t update default workspace"
      );
    }
  };

  const handleCreated = async (org) => {
    const created = org
      ? {
          org_id: org.org_id,
          name: org.name,
          description: org.description,
          avatar_url: org.avatar_url || null,
          plan: org.plan || null,
        }
      : null;

    if (created?.org_id) {
      setOrganizations((prev) => {
        if (prev.some((item) => item.org_id === created.org_id)) {
          return prev;
        }
        return [...prev, created];
      });
      if (org?.start_workspace_tour) {
        markPendingTour(created.org_id);
      }
    }

    setCreateOpen(false);
    setMenuOpen(false);
    if (isMobile) {
      setOpenMobile(false);
    }
    if (created?.org_id) {
      setSelectedOrgId(created.org_id);
      navigate(`/orgs/${encodeURIComponent(created.org_id)}`);
    } else {
      navigate("/dashboard");
    }
  };

  const starButton = ({ isDefault, label, onActivate }) => (
    <DropdownMenuItem
      className="cursor-pointer px-2"
      aria-label={
        isDefault ? `${label} is default on login` : `Set ${label} as default on login`
      }
      title={isDefault ? "Default on login" : "Set as default on login"}
      onSelect={(event) => {
        event.preventDefault();
        onActivate();
      }}
    >
      <Star
        className={cn(
          "h-4 w-4",
          isDefault ? "fill-primary text-primary" : "text-muted-foreground"
        )}
      />
    </DropdownMenuItem>
  );

  const triggerButton = (
    <Button
      variant="outline"
      data-tour="workspace-switcher"
      aria-label={`Workspace switcher: ${activeName}. ${activeSubtitle}`}
      aria-expanded={menuOpen}
      aria-haspopup="menu"
      className={cn(
        "border-sidebar-border bg-sidebar-accent/40 shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        sidebarCollapsed
          ? "size-10 justify-center p-0"
          : "h-auto w-full justify-between gap-2 px-3 py-2.5"
      )}
    >
      {sidebarCollapsed ? (
        isSocial ? (
          <MessageCircle className="h-5 w-5 shrink-0 text-primary" />
        ) : (
          <OrganizationAvatar
            org={activeOrg}
            className="h-7 w-7"
            fallbackClassName="text-[10px]"
          />
        )
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              {isSocial ? (
                <MessageCircle className="h-5 w-5" />
              ) : (
                <OrganizationAvatar
                  org={activeOrg}
                  className="h-9 w-9"
                  fallbackClassName="text-xs"
                />
              )}
            </span>
            <span className="min-w-0 flex-1 text-left">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Workspace
              </span>
              <span className="block truncate text-sm font-semibold text-sidebar-foreground">
                {loading && !activeOrg && !isSocial ? "Loading…" : activeName}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {error || activeSubtitle}
              </span>
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </>
      )}
    </Button>
  );

  return (
    <div
      className={cn(
        "w-full",
        sidebarCollapsed ? "flex justify-center px-1 py-1" : "px-2 py-2"
      )}
    >
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {sidebarCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              {activeName} · {activeSubtitle}
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          align="start"
          side={sidebarCollapsed ? "right" : "bottom"}
          className="w-[280px]"
        >
          <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Circa Social
          </DropdownMenuLabel>
          <div className="flex items-stretch gap-0.5">
            <DropdownMenuItem
              onClick={() => handleSelect("social")}
              className="min-w-0 flex-1 cursor-pointer"
            >
              <MessageCircle className="mr-2 h-4 w-4 text-primary" />
              <span className="flex-1 truncate">Circa Social</span>
              {isSocial && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
            {starButton({
              isDefault: isSocialDefault,
              label: "Circa Social",
              onActivate: () => handleSetDefault({ type: "social" }),
            })}
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Workspaces
          </DropdownMenuLabel>

          {loading && (
            <DropdownMenuItem disabled className="gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading workspaces…
            </DropdownMenuItem>
          )}

          {!loading && error && (
            <DropdownMenuItem disabled className="text-destructive">
              {error}
            </DropdownMenuItem>
          )}

          {!loading && !error && organizations.length === 0 && (
            <DropdownMenuItem disabled>
              No workspaces yet — create one below
            </DropdownMenuItem>
          )}

          {!loading &&
            !error &&
            sortedOrganizations.map((org) => {
              const selected = selectedOrgId === org.org_id;
              const isDefault = defaultOrgId === org.org_id;
              return (
                <div key={org.org_id} className="flex items-stretch gap-0.5">
                  <DropdownMenuItem
                    onClick={() => handleSelect(org.org_id)}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <OrganizationAvatar
                      org={org}
                      className="mr-2 h-5 w-5"
                      fallbackClassName="text-[8px]"
                    />
                    <span className="min-w-0 flex-1 truncate">{org.name}</span>
                    <OrgPlanBadge plan={org.plan} className="mr-1" />
                    {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </DropdownMenuItem>
                  {starButton({
                    isDefault,
                    label: org.name,
                    onActivate: () =>
                      handleSetDefault({
                        type: "organization",
                        org_id: org.org_id,
                      }),
                  })}
                </div>
              );
            })}

          {defaultError && (
            <p className="px-2 py-1 text-xs text-destructive">{defaultError}</p>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            data-tour="workspace-create-org"
            onSelect={(event) => {
              event.preventDefault();
              setCreateOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateOrganizationWizard
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default WorkspaceSwitcher;
